
import Vue from 'vue';
import { Modal } from '@descomplica/vue-components';
import DefaultStudyPlanningCreationFirstStep from './partials/DefaultStudyPlanningCreationFirstStep.vue';
import DefaultStudyPlanningCreationLoadingStep from './partials/DefaultStudyPlanningCreationLoadingStep.vue';
import DefaultStudyPlanningCreationLastStep from './partials/DefaultStudyPlanningCreationLastStep.vue';
import { StudyPlanningBreakpointModel } from '@/models/studyPlanning/StudyPlanningBreakpointModel';
import { CourseModel } from '@descomplica/dex-models';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import Analytics from '@/helpers/analytics';
import { showPostgradErrorToast } from '@/helpers/postgradErrorToast';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';
import { buildDefaultStudyPlanningModalHeightsGenerator } from '@/helpers/postgradStudyPlanning/defaultStudyPlanningModalAnimation';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';

interface Data {
  step: number;
  isCreatingStudyPlanning: boolean;
}

export default Vue.extend({
  components: {
    Modal,
    DefaultStudyPlanningCreationFirstStep,
    DefaultStudyPlanningCreationLoadingStep,
    DefaultStudyPlanningCreationLastStep,
  },
  props: {
    shouldRetry: {
      type: Boolean,
      default: false,
    },
    showDefaultStudyPlanningModal: {
      required: true,
      type: Boolean,
    },
    possibleStudyPlanningBreakpoints: {
      type: Array as Vue.PropType<StudyPlanningBreakpointModel[]>,
      required: true,
    },
  },
  data(): Data {
    return {
      step: 1,
      isCreatingStudyPlanning: false,
    };
  },
  computed: {
    defaultMonthsOption(): number {
      return (this.possibleStudyPlanningBreakpoints[1] ?? this.possibleStudyPlanningBreakpoints[0])
        .numberOfMonths;
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
    isTrial(): boolean {
      return !!this.$store.getters.trialByDescomplicaCourseId(this.currentChimeraCourse?._id);
    },
    modalContainerElement(): HTMLElement | null {
      return (this.$refs.modal as Vue).$el.querySelector('.modal__container') as HTMLElement;
    },
    modalHeightsGenerator(): Generator<
      { mobile: string; tablet: string; desktop: string },
      void,
      unknown
    > {
      return buildDefaultStudyPlanningModalHeightsGenerator(this.isTrial);
    },
    screenBreakpoint(): 'mobile' | 'tablet' | 'desktop' {
      if (window.innerWidth < 495) return 'mobile';
      if (window.innerWidth < 1024) return 'tablet';
      return 'desktop';
    },
  },
  watch: {
    shouldRetry: {
      handler(value: boolean) {
        if (value) {
          this.createDefaultStudyPlanning();
        }
      },
    },
  },
  methods: {
    closeDefaultStudyPlanningModal(): void {
      this.$emit('onClose');
    },
    async createDefaultStudyPlanning(): Promise<void> {
      this.isCreatingStudyPlanning = true;

      this.setNextModalHeight();

      try {
        await this.$store.dispatch('SAVE_STUDY_PLANNING', {
          courseId: this.currentCourse.id,
          numberOfMonths: this.defaultMonthsOption,
        });

        Analytics.trackEvent(EVENT_LABELS.postgradStudyPlanningSaved, {
          in_default_postgrad_studyplanning: true,
          chosen_months_option: this.defaultMonthsOption,
          course: this.currentCourse.name,
          ...this.getTrialStatusByDescomplicaCourseId,
        });
      } catch (err) {
        showPostgradErrorToast(err as Error, this.$t('saveStudyPlanningError').toString());
        this.$emit('defaultStudyPlanningError');

        Analytics.trackEvent(EVENT_LABELS.savePostgradStudyPlanningError, {
          in_default_postgrad_studyplanning: true,
          course: this.currentCourse.name,
          ...this.getTrialStatusByDescomplicaCourseId,
        });

        this.closeDefaultStudyPlanningModal();
      }

      this.$emit('studyPlanningCreated');
      this.isCreatingStudyPlanning = false;

      if (!this.isTrial) {
        this.step = 2;
        this.setNextModalHeight();
        return;
      }

      this.closeDefaultStudyPlanningModal();
    },
    trackCloseAttemptEvent(): void {
      Analytics.trackEvent(EVENT_LABELS.modalCloseAttempt, {
        in_default_postgrad_studyplanning: true,
        course: this.currentCourse.name,
        ...this.getTrialStatusByDescomplicaCourseId,
      });
    },
    setNextModalHeight(): void {
      const next = this.modalHeightsGenerator.next();

      if (!this.modalContainerElement || next.done) return;

      this.modalContainerElement.style.height = next.value[this.screenBreakpoint];
    },
  },
});
